<template>
  <page-main title="Interview Studio" noBottom>
    <template v-slot:aside>
      <div>
        <p class="font-weight-bold mb-2">
          It's hard to know what to say in an interview.
        </p>
        <p>
          You've got the experience — the hard part is recalling the right
          story, in the right shape, under pressure.
        </p>
        <p>
          Paste a job description to target the questions, or leave it
          blank for general questions based on your profile. Either way we
          generate likely interview questions and draft STAR-format answers
          <span class="font-weight-bold"
            >using only the achievements already on your profile</span
          >
          — never inventing.
        </p>
        <p class="font-weight-bold mt-3 mb-1">How to use it:</p>
        <ol>
          <li>
            Paste a JD to target the questions, or leave it blank for
            general ones. Add any extra context - what the recruiter told
            you, what you want to emphasize, what you're nervous about.
          </li>
          <li>Pick how many questions — 8 to 12 is the sweet spot.</li>
          <li>
            Each answer cites the resume bullet (or small win) it came
            from. <span class="font-weight-bold">Green chip</span> =
            grounded. <span class="font-weight-bold">Amber</span> = partial
            match. <span class="font-weight-bold">Grey</span> = no match →
            answer generically or add a relevant accomplishment first.
          </li>
        </ol>
        <p class="text-caption text--secondary mt-3 mb-0">
          Sessions aren't saved server-side yet — copy down what you want
          to keep.
        </p>
        <div class="aside-chips mt-3">
          <span class="aside-chip">★ Plus only</span>
        </div>
      </div>
    </template>
    <template v-slot:nav>
      <modal cardTitle="Tips" ctaText="Need a few tips?" :accentButton="true">
        <ul>
          <li>
            <span class="font-weight-bold">Tip #1:</span> Have a JD? Paste
            the whole thing - title, responsibilities, qualifications. We
            use the full text to pick which questions are most likely. No
            JD? Leave it blank and we generate general questions from your
            profile.
          </li>
          <li>
            <span class="font-weight-bold">Tip #2:</span> Use Additional
            Context for anything not in the JD — what the recruiter told
            you, what you're nervous about, what you want to emphasize, or
            specific topics you expect to come up.
          </li>
          <li>
            <span class="font-weight-bold">Tip #3:</span> Watch the
            grounding chip on each question. Green = backed by a real
            bullet. Amber = partial match. Grey = no match — answer
            generically or add a relevant accomplishment first.
          </li>
        </ul>
      </modal>
    </template>

    <v-container fluid class="container-main">
      <!-- ═════════════════ LOADING STATUS ═════════════════ -->
      <v-row v-if="!statusLoaded">
        <v-col cols="12" class="text-center">
          <v-progress-circular
            indeterminate
            color="primary"
          ></v-progress-circular>
        </v-col>
      </v-row>

      <!-- ═════════════════ LOCKED (NOT PREMIUM) ═════════════════ -->
      <v-row v-else-if="!unlocked">
        <v-col cols="12">
          <v-card outlined class="pa-6 text-center">
            <v-icon size="56" color="primary" class="mb-3"
              >mdi-microphone</v-icon
            >
            <div class="text-h6 font-weight-medium mb-2">
              Interview Studio is a Plus feature
            </div>
            <div class="text-body-2 text--secondary mb-4">
              Paste any JD. Get likely questions plus STAR answers drawn
              from your actual achievements — never invented.
            </div>
            <v-btn color="primary" @click="goToUpgrade">
              Unlock with Resumation Plus
            </v-btn>
          </v-card>
        </v-col>
      </v-row>

      <!-- ═════════════════ NEEDS MORE SUBSTRATE ═════════════════ -->
      <v-row v-else-if="needsMoreSubstrate">
        <v-col cols="12">
          <v-card outlined class="pa-6 text-center">
            <v-icon size="56" color="amber darken-2" class="mb-3"
              >mdi-clipboard-list-outline</v-icon
            >
            <div class="text-h6 font-weight-medium mb-2">
              Your profile needs more substrate
            </div>
            <div class="text-body-2 text--secondary mb-4">
              You have {{ achievementCount }} accomplishment{{
                achievementCount === 1 ? "" : "s"
              }}
              on your profile. We need at least
              {{ readinessThreshold }} to ground real STAR answers — otherwise
              we'd have to invent, and we won't do that.
            </div>
            <v-btn color="primary" @click="goToWork">
              Add a few accomplishments first
            </v-btn>
          </v-card>
        </v-col>
      </v-row>

      <!-- ═════════════════ MAIN FORM + RESULTS ═════════════════ -->
      <template v-else>
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="companyName"
              label="Company Name (optional)"
              dense
              outlined
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="roleTitle"
              label="Role Title (optional)"
              dense
              outlined
            ></v-text-field>
          </v-col>
          <v-col cols="12">
            <v-textarea
              v-model="jobDescription"
              label="Job Description (optional)"
              rows="10"
              outlined
              :counter="12000"
              hint="Optional - paste a job description to target the questions, or leave blank for general questions based on your profile."
              persistent-hint
              :rules="[v => (!v || v.length <= 12000) || 'Max 12,000 characters']"
            ></v-textarea>
          </v-col>
          <v-col cols="12">
            <v-textarea
              v-model="recruiterNotes"
              label="Additional Context (optional)"
              rows="3"
              outlined
              :counter="4000"
              hint="Anything else about this role or your situation that helps tailor the questions — what the recruiter told you, your own concerns, what you want to emphasize, etc."
              persistent-hint
            ></v-textarea>
          </v-col>
          <v-col cols="12" md="4">
            <v-select
              v-model.number="questionCount"
              :items="questionCountOptions"
              label="How many questions?"
              dense
              outlined
            ></v-select>
          </v-col>
          <v-col cols="12" md="8" class="d-flex align-center justify-end">
            <v-btn
              color="primary"
              :disabled="!canSubmit || isGenerating"
              :loading="isGenerating"
              @click="onGenerate"
            >
              <v-icon left>mdi-sparkles</v-icon>
              Generate Prep
            </v-btn>
          </v-col>
        </v-row>

        <!-- ─── Error banner ─── -->
        <v-row v-if="lastError">
          <v-col cols="12">
            <v-alert type="error" text dense>{{ lastError }}</v-alert>
          </v-col>
        </v-row>

        <!-- ─── Results ─── -->
        <v-row v-if="lastSession && questions.length">
          <v-col cols="12">
            <v-divider class="mb-3"></v-divider>
            <div class="d-flex align-center justify-space-between mb-3 flex-wrap" style="gap:8px;">
              <div>
                <span class="text-h6 font-weight-medium"
                  >{{ questions.length }} interview question{{
                    questions.length === 1 ? "" : "s"
                  }}</span
                >
                <span v-if="sessionLabel" class="text-body-2 text--secondary ml-2"
                  >for {{ sessionLabel }}</span
                >
              </div>
              <div>
                <v-chip
                  v-if="lastSession.groundedCount > 0"
                  small
                  color="success"
                  outlined
                  class="mr-1"
                >
                  <v-icon left small>mdi-shield-check</v-icon>
                  {{ lastSession.groundedCount }} fully grounded
                </v-chip>
                <v-chip
                  v-if="lastSession.partialCount > 0"
                  small
                  color="amber darken-2"
                  outlined
                  class="mr-1"
                >
                  <v-icon left small>mdi-shield-half-full</v-icon>
                  {{ lastSession.partialCount }} partial
                </v-chip>
                <v-chip
                  v-if="lastSession.ungroundedCount > 0"
                  small
                  outlined
                  color="grey"
                  class="mr-1"
                >
                  {{ lastSession.ungroundedCount }} needs proof
                </v-chip>
                <!-- PR-INTERVIEW-COPY: copy-all so the user can dump the
                     whole session into a doc / notes app. Plain-text
                     conversational format — see formatSession() helper. -->
                <v-btn
                  small
                  outlined
                  color="primary"
                  class="ml-1"
                  @click="copySession"
                >
                  <v-icon left small>{{
                    copyAllJustDone
                      ? "mdi-check"
                      : "mdi-content-copy"
                  }}</v-icon>
                  {{ copyAllJustDone ? "Copied" : "Copy all" }}
                </v-btn>
              </div>
            </div>
          </v-col>

          <v-col
            v-for="(q, i) in questions"
            :key="q.id || 'q-' + i"
            cols="12"
          >
            <v-card outlined class="pa-3 question-card">
              <div class="d-flex align-start" style="gap:8px;">
                <div class="flex-grow-1">
                  <div class="text-body-1 font-weight-medium mb-1">
                    {{ i + 1 }}. {{ q.questionText }}
                  </div>
                  <div class="d-flex flex-wrap" style="gap:6px;">
                    <v-chip
                      v-if="q.questionType"
                      x-small
                      outlined
                      color="secondary"
                      >{{ titleCase(q.questionType) }}</v-chip
                    >
                    <v-chip
                      x-small
                      outlined
                      :color="groundednessColor(q.groundedness)"
                    >
                      <v-icon left x-small>{{
                        groundednessIcon(q.groundedness)
                      }}</v-icon>
                      {{ groundednessLabel(q.groundedness) }}
                    </v-chip>
                  </div>
                </div>
                <!-- PR-INTERVIEW-COPY: per-question copy. Disabled on
                     ungrounded entries since there's nothing useful to
                     paste — leaves the user with a clear signal that
                     "this one has no answer yet." -->
                <v-btn
                  icon
                  x-small
                  :disabled="q.groundedness === 'none'"
                  :title="
                    q.groundedness === 'none'
                      ? 'No answer to copy — add a relevant achievement first'
                      : 'Copy question + STAR answer'
                  "
                  @click="copyQuestion(q, i)"
                >
                  <v-icon small>{{
                    copiedQuestionIndex === i
                      ? "mdi-check"
                      : "mdi-content-copy"
                  }}</v-icon>
                </v-btn>
              </div>

              <!-- STAR answer (or ungrounded note) -->
              <div v-if="q.groundedness === 'none'" class="ungrounded-note mt-2">
                <v-icon small color="grey" class="mr-1"
                  >mdi-information-outline</v-icon
                >
                <span class="text-body-2">{{ q.ungroundedNote }}</span>
              </div>
              <div v-else class="mt-2">
                <div
                  v-for="slot in starSlots"
                  :key="'slot-' + i + '-' + slot.key"
                  class="star-row mb-2"
                >
                  <div class="star-label">{{ slot.label }}</div>
                  <div class="star-text">
                    {{ q.starAnswer && q.starAnswer[slot.key] || "—" }}
                  </div>
                </div>
              </div>

              <!-- Sources -->
              <div
                v-if="q.sourceBulletTexts && q.sourceBulletTexts.length"
                class="sources mt-2"
              >
                <div class="text-caption text--secondary mb-1">
                  Drawn from:
                </div>
                <div
                  v-for="(bullet, bi) in q.sourceBulletTexts"
                  :key="'src-' + i + '-' + bi"
                  class="source-row"
                >
                  <v-icon x-small color="success" class="mr-1"
                    >mdi-source-branch</v-icon
                  >
                  <span class="text-caption">
                    <span class="font-weight-bold"
                      >{{ q.sourceContextLabels && q.sourceContextLabels[bi] || "Your profile" }}:</span
                    >
                    {{ bullet }}
                  </span>
                </div>
              </div>

              <!-- Unsupported claims (transparency) -->
              <v-alert
                v-if="q.unsupportedClaims && q.unsupportedClaims.length"
                type="warning"
                text
                dense
                class="mt-2 mb-0"
              >
                <div class="text-caption font-weight-medium">
                  Not yet supported by your profile:
                </div>
                <ul class="ma-0 pa-0 pl-4">
                  <li
                    v-for="(c, ci) in q.unsupportedClaims"
                    :key="'uc-' + i + '-' + ci"
                    class="text-caption"
                  >
                    {{ c }}
                  </li>
                </ul>
              </v-alert>
            </v-card>
          </v-col>
        </v-row>

        <!-- Empty results state -->
        <v-row v-if="lastSession && !questions.length && !isGenerating">
          <v-col cols="12">
            <v-alert type="info" text dense>
              We couldn't generate any grounded questions yet. Your profile
              substrate may be too thin to ground real answers - try adding
              a few more accomplishments, or paste a job description to give
              us more to work with.
            </v-alert>
          </v-col>
        </v-row>
      </template>
    </v-container>
  </page-main>
</template>

<script>
import { mapActions, mapState, mapGetters } from "vuex";
import Modal from "@/components/Modal.vue";

const STAR_SLOTS = [
  { key: "situation", label: "Situation" },
  { key: "task", label: "Task" },
  { key: "action", label: "Action" },
  { key: "result", label: "Result" }
];

export default {
  name: "InterviewStudio",
  components: { Modal },
  data: () => ({
    companyName: "",
    roleTitle: "",
    jobDescription: "",
    recruiterNotes: "",
    questionCount: 10,
    questionCountOptions: [5, 8, 10, 12, 15],
    starSlots: STAR_SLOTS,
    // PR-INTERVIEW-COPY: transient UI state for copy-button feedback.
    // Both clear after 2s so the icon flips back to mdi-content-copy.
    copiedQuestionIndex: -1,
    copyAllJustDone: false
  }),
  computed: {
    ...mapState("interviewStudio", [
      "statusLoaded",
      "unlocked",
      "isPremium",
      "achievementCount",
      "readinessThreshold",
      "lastSession",
      "isGenerating",
      "lastError"
    ]),
    ...mapGetters("interviewStudio", ["canGenerate", "needsMoreSubstrate"]),
    questions() {
      return (this.lastSession && this.lastSession.questions) || [];
    },
    canSubmit() {
      // JD is optional: blank JD generates generic profile-grounded
      // questions. We only block on the length cap when a JD is present.
      return (
        this.canGenerate &&
        (!this.jobDescription || this.jobDescription.length <= 12000)
      );
    },
    sessionLabel() {
      if (!this.lastSession) return "";
      const parts = [];
      if (this.lastSession.roleTitle) parts.push(this.lastSession.roleTitle);
      if (this.lastSession.companyName)
        parts.push("at " + this.lastSession.companyName);
      return parts.join(" ");
    }
  },
  async mounted() {
    await this.refreshStatus();
  },
  methods: {
    ...mapActions("interviewStudio", ["refreshStatus", "generate"]),
    async onGenerate() {
      if (!this.canSubmit) return;
      await this.generate({
        jobDescription: this.jobDescription,
        recruiterNotes: this.recruiterNotes,
        companyName: this.companyName,
        roleTitle: this.roleTitle,
        questionCount: this.questionCount
      });
    },
    goToUpgrade() {
      // PR-UPGRADE-MODAL: open the one shared upgrade modal instead of hard
      // redirecting to /Upgrade, so the prompt looks identical everywhere.
      // Gating (the locked card shows only when !unlocked) is unchanged.
      this.$store.dispatch("upgradeModal/open", {
        featureName: "Interview Studio",
        message:
          "Interview Studio is part of the AI tools. Plus turns them on, along with unlimited Sage coaching, whenever you want more."
      });
    },
    goToWork() {
      this.$router.push({ path: "/Work" });
    },
    titleCase(str) {
      if (!str) return "";
      return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    },
    groundednessColor(g) {
      switch (g) {
        case "full":
          return "success";
        case "partial":
          return "amber darken-2";
        default:
          return "grey";
      }
    },
    groundednessIcon(g) {
      switch (g) {
        case "full":
          return "mdi-shield-check";
        case "partial":
          return "mdi-shield-half-full";
        default:
          return "mdi-shield-off-outline";
      }
    },
    groundednessLabel(g) {
      switch (g) {
        case "full":
          return "Fully grounded";
        case "partial":
          return "Partial match";
        default:
          return "Needs proof";
      }
    },

    // ═══════════ PR-INTERVIEW-COPY: clipboard helpers ═══════════
    // Two surfaces: per-question copy (button on each card) and
    // session-level copy (button in the results header). Both produce
    // a conversational plain-text format the user can paste into
    // notes / a doc / wherever they're prepping. Sources are included
    // as a footer line so the user can see WHICH bullet each answer
    // came from. Ungrounded questions are filtered out of "Copy all"
    // (and the per-question button is disabled for them) — there's
    // nothing useful to paste.
    formatQuestion(q, index) {
      var lines = [];
      var label = index != null ? (index + 1) + ". " : "";
      lines.push(label + (q.questionText || "").trim());
      lines.push("");
      var slots = ["situation", "task", "action", "result"];
      var labels = {
        situation: "Situation",
        task: "Task",
        action: "Action",
        result: "Result"
      };
      var sa = q.starAnswer || {};
      for (var i = 0; i < slots.length; i++) {
        var key = slots[i];
        var val = (sa[key] || "").trim();
        if (val) {
          lines.push(labels[key] + ": " + val);
        }
      }
      // Sources footer — only when present so we don't leave a dangling
      // "Drawn from:" with nothing under it.
      if (q.sourceBulletTexts && q.sourceBulletTexts.length) {
        lines.push("");
        lines.push("Drawn from:");
        for (var bi = 0; bi < q.sourceBulletTexts.length; bi++) {
          var ctx =
            (q.sourceContextLabels && q.sourceContextLabels[bi]) ||
            "Your profile";
          lines.push("• " + ctx + ": " + q.sourceBulletTexts[bi]);
        }
      }
      return lines.join("\n");
    },
    formatSession() {
      if (!this.questions || !this.questions.length) return "";
      var header = "Interview prep";
      if (this.sessionLabel) header += " for " + this.sessionLabel;
      var blocks = [header, ""];
      var includable = this.questions.filter(function(q) {
        return q.groundedness !== "none";
      });
      for (var i = 0; i < includable.length; i++) {
        blocks.push(this.formatQuestion(includable[i], i));
        blocks.push("");
        blocks.push("───");
        blocks.push("");
      }
      // Trim trailing separator block.
      while (blocks.length && !blocks[blocks.length - 1].trim()) blocks.pop();
      if (blocks.length && blocks[blocks.length - 1] === "───") blocks.pop();
      return blocks.join("\n").trim();
    },
    async copyToClipboard(text) {
      if (!text) return false;
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text);
          return true;
        }
      } catch (e) {
        // Fall through to legacy path.
      }
      try {
        var ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        var ok = document.execCommand("copy");
        document.body.removeChild(ta);
        return ok;
      } catch (e) {
        return false;
      }
    },
    async copyQuestion(q, index) {
      var text = this.formatQuestion(q, index);
      var ok = await this.copyToClipboard(text);
      if (!ok) return;
      this.copiedQuestionIndex = index;
      var self = this;
      setTimeout(function() {
        if (self.copiedQuestionIndex === index) {
          self.copiedQuestionIndex = -1;
        }
      }, 2000);
    },
    async copySession() {
      var text = this.formatSession();
      var ok = await this.copyToClipboard(text);
      if (!ok) return;
      this.copyAllJustDone = true;
      var self = this;
      setTimeout(function() {
        self.copyAllJustDone = false;
      }, 2000);
    }
  }
};
</script>

<style scoped>
.question-card {
  border-left: 3px solid #6b21a8;
}
.star-row {
  display: grid;
  grid-template-columns: 90px 1fr;
  gap: 8px;
  align-items: start;
}
.star-label {
  font-size: 12px;
  font-weight: 600;
  color: #6b21a8;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  padding-top: 2px;
}
.star-text {
  font-size: 14px;
  line-height: 1.45;
}
.sources {
  border-top: 1px dashed rgba(160, 160, 160, 0.4);
  padding-top: 8px;
}
.source-row {
  display: flex;
  align-items: flex-start;
  margin-bottom: 4px;
}
.ungrounded-note {
  padding: 8px 12px;
  background: rgba(160, 160, 160, 0.1);
  border-radius: 6px;
}
.aside-chips {
  margin-top: 8px;
}
.aside-chip {
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  background: #f3e8ff;
  color: #6b21a8;
  margin-right: 6px;
}
@media (max-width: 600px) {
  .star-row {
    grid-template-columns: 1fr;
  }
}
</style>
